import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { translateNotification } from '../utils/i18nHelpers';
import { formatDate } from '../utils/dateFormat';
import ChangeApprovalModal from './ChangeApprovalModal';

const NotificationBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approvalLogId, setApprovalLogId] = useState(null); // id-ul change log deschis în modal
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Polling la fiecare 30 secunde
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Închide dropdown când se face click afară
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      // Silently fail — nu vrem erori vizibile pentru notificări
    }
  };

  const handleMarkAsRead = async (notifId, e) => {
    e.stopPropagation();
    try {
      await api.put(`/notifications/${notifId}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === notifId ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {}
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await api.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (notifId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${notifId}`);
      const notif = notifications.find((n) => n.id === notifId);
      setNotifications((prev) => prev.filter((n) => n.id !== notifId));
      if (notif && !notif.read) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {}
  };

  const handleNotifClick = async (notif) => {
    // Marchează ca citită
    if (!notif.read) {
      await api.put(`/notifications/${notif.id}/read`).catch(() => {});
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    // Navighează la pagina relevantă
    setOpen(false);
    if (notif.type === 'SIGNATURE_TURN') {
      // Raport ITM — routare către tab-ul ITM al firmei respective
      if (notif.data?.context === 'ITM' && notif.data?.companyId) {
        navigate(`/companies/${notif.data.companyId}?tab=itm`);
      } else {
        navigate('/sheets-to-sign?pending=true');
      }
    } else if (notif.type === 'TRAINING_ASSIGNED') {
      navigate('/my-courses');
    } else if (notif.type === 'IP_REMINDER') {
      navigate('/employees?tab=ip-due');
    } else if (notif.type === 'PASSWORD_REMINDER') {
      navigate('/my-profile');
    } else if (
      (notif.type === 'CHANGE_INFO' || notif.type === 'CHANGE_SIGNED') &&
      notif.data?.changeLogId
    ) {
      // View-only pentru responsabili: vezi declarația semnată de angajat (sau inițiator)
      setApprovalLogId(notif.data.changeLogId);
    } else if (notif.type === 'CHANGE_EMPLOYEE_SIGN' && notif.data?.changeLogId) {
      try {
        const res = await api.get(`/change-logs/${notif.data.changeLogId}`);
        if (res.data?.status === 'APPROVED') {
          navigate('/my-certificates');
        } else if (res.data?.status === 'REJECTED') {
          navigate('/my-profile');
        } else {
          navigate('/dashboard');
        }
      } catch (_) {
        navigate('/dashboard');
      }
    } else if (notif.type === 'CHANGE_REFUSED' && notif.data?.changeLogId) {
      // Inițiatorul vede de ce a refuzat angajatul — view-only modal cu motivul
      setApprovalLogId(notif.data.changeLogId);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return t('notifications.justNow');
    if (diff < 3600) return t('notifications.minutesAgo', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('notifications.hoursAgo', { count: Math.floor(diff / 3600) });
    if (diff < 604800) return t('notifications.daysAgo', { count: Math.floor(diff / 86400) });
    return formatDate(date);
  };

  const getTypeIcon = (type) => {
    if (type === 'SIGNATURE_TURN') {
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
          />
        </svg>
      );
    }
    if (type === 'TRAINING_ASSIGNED') {
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      );
    }
    if (type === 'IP_REMINDER') {
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    }
    if (type === 'PASSWORD_REMINDER') {
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
      );
    }
    return (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
        />
      </svg>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {approvalLogId && (
        <ChangeApprovalModal
          changeLogId={approvalLogId}
          onClose={() => setApprovalLogId(null)}
          onDone={() => {
            setApprovalLogId(null);
            fetchNotifications();
          }}
        />
      )}
      {/* Buton clopotel */}
      <button
        type="button"
        aria-label="Notificări"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge număr */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-5 h-5 bg-danger-600 text-white text-xs font-bold rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-surface rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 z-50 overflow-hidden">
          {/* Header dropdown */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-surface-secondary">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                {t('notifications.title')}
              </span>
              {unreadCount > 0 && (
                <span className="bg-danger-100 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300 text-xs px-2 py-0.5 rounded-full font-medium">
                  {t('notifications.newBadge', { count: unreadCount })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={loading}
                  className="text-xs text-primary-600 dark:text-primary-300 hover:text-primary-800 font-medium"
                >
                  {t('notifications.markAll')}
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
                className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                {t('notifications.viewAllShort')}
                <svg
                  className="w-3 h-3 inline ml-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Lista notificări */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-10 text-slate-400 dark:text-slate-400">
                <svg
                  className="w-8 h-8 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
                <p className="text-sm">{t('notifications.noneShort')}</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 dark:border-slate-800 cursor-pointer transition hover:bg-surface-secondary ${!notif.read ? 'bg-primary-50 dark:bg-primary-950/50' : ''}`}
                >
                  {/* Icon tip */}
                  <div
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${!notif.read ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}
                  >
                    {getTypeIcon(notif.type)}
                  </div>

                  {/* Conținut */}
                  <div className="flex-1 min-w-0">
                    {(() => {
                      const { title: tTitle, message: tMessage } = translateNotification(t, notif);
                      return (
                        <>
                          <p
                            className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}
                          >
                            {tTitle}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                            {tMessage}
                          </p>
                        </>
                      );
                    })()}
                    <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                      {formatTime(notif.createdAt)}
                    </p>
                  </div>

                  {/* Acțiuni */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {!notif.read && (
                      <button
                        onClick={(e) => handleMarkAsRead(notif.id, e)}
                        className="w-2 h-2 bg-primary-500 rounded-full hover:bg-primary-700"
                        title={t('notifications.markAsReadTitle')}
                      />
                    )}
                    <button
                      onClick={(e) => handleDelete(notif.id, e)}
                      className="text-slate-300 dark:text-slate-600 hover:text-danger-400 mt-1"
                      title={t('notifications.deleteTitle')}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div className="px-4 py-2 bg-surface-secondary border-t border-slate-100 dark:border-slate-800 text-center">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
                className="text-xs text-primary-600 dark:text-primary-300 hover:text-primary-800 font-medium"
              >
                {t('notifications.viewAllCount', { count: notifications.length })}
                <svg
                  className="w-3 h-3 inline ml-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
